import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LogOut, Trash2, Plus, Users, IndianRupee, Mail, Image as ImageIcon, Calendar, Upload } from "lucide-react";

const AREA_LABELS = {
  general: "General",
  a: "A. Help for the Poor",
  b: "B. Welfare of the Elderly",
  c: "C. Education",
  d: "D. Medical Aid",
  e: "E. Women & Child Welfare",
  f: "F. Divyang Welfare",
  g: "G. Rural Development",
  h: "H. Environment Protection",
  i: "I. Disaster Relief",
};

const emptyDonor = { name: "", email: "", phone: "", amount: 0, purpose: "General Donation", payment_mode: "offline", notes: "", show_publicly: true };
const emptyEvent = { title: "", title_hi: "", description: "", description_hi: "", date: "", location: "", image_url: "" };
const emptyGallery = { title: "", title_hi: "", image_url: "", category: "a" };

const fileToDataUrl = (file, maxDim = 1400) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new window.Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const AdminDashboard = () => {
  const { t, token, setToken } = useApp();
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [messages, setMessages] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [stats, setStats] = useState({ total_amount: 0, total_donors: 0 });
  const [donorForm, setDonorForm] = useState(emptyDonor);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [galleryForm, setGalleryForm] = useState(emptyGallery);

  const H = authHeaders(token);

  const loadAll = useCallback(async () => {
    try {
      const [d, e, g, m, s, v] = await Promise.all([
        api.get("/donors", H),
        api.get("/events"),
        api.get("/gallery"),
        api.get("/contact", H),
        api.get("/donors/stats"),
        api.get("/volunteers", H),
      ]);
      setDonors(d.data); setEvents(e.data); setGallery(g.data); setMessages(m.data); setStats(s.data); setVolunteers(v.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken(""); navigate("/admin/login");
      }
    }
  }, [token, navigate, setToken]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const logout = () => { setToken(""); navigate("/"); };

  const showErr = (err, fallback = "Failed") => {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) return toast.error(d.map((x) => x.msg || JSON.stringify(x)).join(", "));
    if (typeof d === "string") return toast.error(d);
    return toast.error(err?.message || fallback);
  };

  const addDonor = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...donorForm, email: donorForm.email || null, phone: donorForm.phone || null, notes: donorForm.notes || null };
      await api.post("/donors", payload, H);
      toast.success("Donor added"); setDonorForm(emptyDonor); loadAll();
    } catch (err) { showErr(err); }
  };
  const delDonor = async (id) => {
    if (!window.confirm("Delete donor?")) return;
    await api.delete(`/donors/${id}`, H); toast.success("Deleted"); loadAll();
  };

  const addEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", eventForm, H);
      toast.success("Event added"); setEventForm(emptyEvent); loadAll();
    } catch (err) { showErr(err); }
  };
  const delEvent = async (id) => {
    if (!window.confirm("Delete event?")) return;
    await api.delete(`/events/${id}`, H); toast.success("Deleted"); loadAll();
  };

  const addGallery = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gallery", galleryForm, H);
      toast.success("Gallery item added"); setGalleryForm(emptyGallery); loadAll();
    } catch (err) { showErr(err); }
  };
  const delGallery = async (id) => {
    if (!window.confirm("Delete item?")) return;
    await api.delete(`/gallery/${id}`, H); toast.success("Deleted"); loadAll();
  };

  const delMessage = async (id) => {
    if (!window.confirm("Delete message?")) return;
    await api.delete(`/contact/${id}`, H); toast.success("Deleted"); loadAll();
  };

  const setVolunteerStatus = async (id, status) => {
    try {
      await api.patch(`/volunteers/${id}?status=${status}`, {}, H);
      toast.success(`Volunteer ${status}`);
      loadAll();
    } catch (err) { showErr(err); }
  };
  const delVolunteer = async (id) => {
    if (!window.confirm("Delete volunteer?")) return;
    await api.delete(`/volunteers/${id}`, H); toast.success("Deleted"); loadAll();
  };

  return (
    <div className="min-h-screen bg-[#F4EFE6]" data-testid="admin-dashboard">
      <div className="bg-[#1B4332] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif-display text-3xl">{t("adminTitle")}</h1>
            <p className="text-xs uppercase tracking-[0.2em] text-[#8FBC8F] mt-1">Dharmoday Seva Sansthan</p>
          </div>
          <Button onClick={logout} data-testid="admin-logout-btn" variant="outline" className="rounded-full bg-transparent border-[#FDFBF7]/40 text-[#FDFBF7] hover:bg-[#C95B24] hover:border-[#C95B24]">
            <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { Icon: Users, label: "Donors", val: stats.total_donors },
            { Icon: IndianRupee, label: "Raised", val: `₹ ${Number(stats.total_amount).toLocaleString("en-IN")}` },
            { Icon: Mail, label: "Messages", val: messages.length },
            { Icon: ImageIcon, label: "Gallery", val: gallery.length },
          ].map((s, i) => (
            <div key={i} className="card-tactile p-6" data-testid={`admin-stat-${i}`}>
              <s.Icon className="w-5 h-5 text-[#C95B24] mb-2" />
              <div className="font-serif-display text-3xl text-[#1B4332]">{s.val}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-[#4A4A4A]">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="donors" className="w-full">
          <TabsList className="mb-6 bg-white border">
            <TabsTrigger value="donors" data-testid="tab-donors">Donors</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="tab-volunteers">Volunteers</TabsTrigger>
          </TabsList>

          {/* DONORS */}
          <TabsContent value="donors">
            <form onSubmit={addDonor} className="card-tactile p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="donor-add-form">
              <div>
                <Label>Name*</Label>
                <Input required value={donorForm.name} onChange={(e) => setDonorForm({ ...donorForm, name: e.target.value })} data-testid="admin-donor-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={donorForm.email} onChange={(e) => setDonorForm({ ...donorForm, email: e.target.value })} data-testid="admin-donor-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={donorForm.phone} onChange={(e) => setDonorForm({ ...donorForm, phone: e.target.value })} data-testid="admin-donor-phone" />
              </div>
              <div>
                <Label>Amount (₹)*</Label>
                <Input type="number" required min="1" value={donorForm.amount} onChange={(e) => setDonorForm({ ...donorForm, amount: Number(e.target.value) })} data-testid="admin-donor-amount" />
              </div>
              <div>
                <Label>Purpose</Label>
                <Input value={donorForm.purpose} onChange={(e) => setDonorForm({ ...donorForm, purpose: e.target.value })} data-testid="admin-donor-purpose" />
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={donorForm.payment_mode} onValueChange={(v) => setDonorForm({ ...donorForm, payment_mode: v })}>
                  <SelectTrigger data-testid="admin-donor-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offline">Offline / Cash / Bank</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Input value={donorForm.notes} onChange={(e) => setDonorForm({ ...donorForm, notes: e.target.value })} data-testid="admin-donor-notes" />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="a-pub" checked={donorForm.show_publicly} onCheckedChange={(v) => setDonorForm({ ...donorForm, show_publicly: !!v })} data-testid="admin-donor-pub" />
                  <Label htmlFor="a-pub" className="text-sm">Public</Label>
                </div>
                <Button type="submit" className="btn-saffron rounded-full ml-auto" data-testid="admin-donor-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
            </form>

            <div className="card-tactile overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email/Phone</TableHead><TableHead>Amount</TableHead>
                    <TableHead>Purpose</TableHead><TableHead>Mode</TableHead><TableHead>Status</TableHead><TableHead>Public</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {donors.map((d) => (
                    <TableRow key={d.id} data-testid={`admin-donor-row-${d.id}`}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell className="text-xs">{d.email}<br />{d.phone}</TableCell>
                      <TableCell>₹ {Number(d.amount).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-xs">{d.purpose}</TableCell>
                      <TableCell><Badge variant="outline">{d.payment_mode}</Badge></TableCell>
                      <TableCell>
                        <Badge className={d.status === "success" ? "bg-green-100 text-green-800" : d.status === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}>{d.status}</Badge>
                      </TableCell>
                      <TableCell>{d.show_publicly ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => delDonor(d.id)} data-testid={`admin-donor-del-${d.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {donors.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#4A4A4A] py-8">No donors yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events">
            <form onSubmit={addEvent} className="card-tactile p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="event-add-form">
              <div><Label>Title (EN)*</Label><Input required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} /></div>
              <div><Label>Title (Hindi)</Label><Input value={eventForm.title_hi} onChange={(e) => setEventForm({ ...eventForm, title_hi: e.target.value })} /></div>
              <div><Label>Date*</Label><Input type="date" required value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} /></div>
              <div className="md:col-span-2">
                <Label>Event Image</Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-[#1B4332]/30 text-sm hover:bg-[#F4EFE6]" data-testid="event-image-upload">
                    <Upload className="w-4 h-4" /> Upload Image
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try { const url = await fileToDataUrl(f); setEventForm(s => ({ ...s, image_url: url })); toast.success("Image ready"); }
                      catch { toast.error("Failed to read image"); }
                    }} />
                  </label>
                  <span className="text-xs text-[#4A4A4A]">or paste URL below</span>
                </div>
                <Input className="mt-2" placeholder="https://..." value={eventForm.image_url?.startsWith("data:") ? "" : (eventForm.image_url || "")} onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })} />
                {eventForm.image_url && <img src={eventForm.image_url} alt="preview" className="mt-2 h-24 rounded-md object-cover" />}
              </div>
              <div className="md:col-span-2"><Label>Description (EN)*</Label><Textarea required value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Description (Hindi)</Label><Textarea value={eventForm.description_hi} onChange={(e) => setEventForm({ ...eventForm, description_hi: e.target.value })} /></div>
              <Button type="submit" className="btn-saffron rounded-full md:col-span-2"><Plus className="w-4 h-4 mr-1" /> Add Event</Button>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {events.map((e) => (
                <div key={e.id} className="card-tactile p-6" data-testid={`admin-event-${e.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs text-[#C95B24] flex items-center gap-1"><Calendar className="w-3 h-3" /> {e.date}</div>
                      <h3 className="font-serif-display text-xl text-[#1B4332] mt-1">{e.title}</h3>
                      <div className="text-xs text-[#4A4A4A]">{e.location}</div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => delEvent(e.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                  <p className="mt-2 text-sm text-[#4A4A4A]">{e.description}</p>
                </div>
              ))}
              {events.length === 0 && <div className="text-[#4A4A4A]">No events yet.</div>}
            </div>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            <form onSubmit={addGallery} className="card-tactile p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gallery-add-form">
              <div><Label>Title*</Label><Input required value={galleryForm.title} onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })} data-testid="admin-gallery-title" /></div>
              <div><Label>Title (Hindi)</Label><Input value={galleryForm.title_hi} onChange={(e) => setGalleryForm({ ...galleryForm, title_hi: e.target.value })} /></div>
              <div className="md:col-span-2">
                <Label>Image*</Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-[#1B4332]/30 text-sm hover:bg-[#F4EFE6]" data-testid="gallery-image-upload">
                    <Upload className="w-4 h-4" /> Upload from device
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try { const url = await fileToDataUrl(f); setGalleryForm(s => ({ ...s, image_url: url })); toast.success("Image ready"); }
                      catch { toast.error("Failed to read image"); }
                    }} />
                  </label>
                  <span className="text-xs text-[#4A4A4A]">or paste URL below</span>
                </div>
                <Input className="mt-2" placeholder="https://..." value={galleryForm.image_url?.startsWith("data:") ? "" : (galleryForm.image_url || "")} onChange={(e) => setGalleryForm({ ...galleryForm, image_url: e.target.value })} data-testid="admin-gallery-url" />
                {galleryForm.image_url && <img src={galleryForm.image_url} alt="preview" className="mt-2 h-32 rounded-md object-cover" />}
              </div>
              <div>
                <Label>Category (Area)</Label>
                <Select value={galleryForm.category} onValueChange={(v) => setGalleryForm({ ...galleryForm, category: v })}>
                  <SelectTrigger data-testid="admin-gallery-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AREA_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end"><Button type="submit" className="btn-saffron rounded-full w-full" data-testid="admin-gallery-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
            </form>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="relative card-tactile overflow-hidden" data-testid={`admin-gallery-${g.id}`}>
                  <img src={g.image_url} alt={g.title} className="w-full aspect-square object-cover" />
                  <button onClick={() => delGallery(g.id)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  <div className="p-2 text-xs">{g.title} <Badge variant="outline" className="ml-1">{AREA_LABELS[g.category]?.split(".")[0] || g.category}</Badge></div>
                </div>
              ))}
              {gallery.length === 0 && <div className="text-[#4A4A4A] col-span-full">No gallery items.</div>}
            </div>
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages">
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="card-tactile p-5" data-testid={`admin-msg-${m.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-[#1B4332]">{m.name} <span className="text-xs text-[#4A4A4A]">· {m.email}{m.phone && ` · ${m.phone}`}</span></div>
                      <div className="text-xs text-[#C95B24] mt-1">{m.subject}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#4A4A4A]">{new Date(m.created_at).toLocaleDateString()}</span>
                      <Button size="sm" variant="ghost" onClick={() => delMessage(m.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-[#4A4A4A]">{m.message}</p>
                </div>
              ))}
              {messages.length === 0 && <div className="text-[#4A4A4A]">No messages yet.</div>}
            </div>
          </TabsContent>

          {/* VOLUNTEERS */}
          <TabsContent value="volunteers">
            <div className="space-y-3">
              {volunteers.map((v) => (
                <div key={v.id} className="card-tactile p-5" data-testid={`admin-volunteer-${v.id}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="font-semibold text-[#1B4332]">
                        {v.name}
                        <span className="ml-2 text-xs text-[#4A4A4A]">· {v.email} · {v.phone}{v.city && ` · ${v.city}`}{v.age && ` · ${v.age}y`}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(v.areas || []).map((a) => (
                          <span key={a} className="text-[10px] uppercase tracking-wider bg-[#F4EFE6] text-[#1B4332] px-2 py-0.5 rounded-full">{a.toUpperCase()}</span>
                        ))}
                        {v.availability && <span className="text-xs text-[#4A4A4A]">· {v.availability}</span>}
                      </div>
                      {v.skills && <div className="text-xs text-[#4A4A4A] mt-1">Skills: {v.skills}</div>}
                      {v.message && <div className="text-sm text-[#4A4A4A] mt-2 italic">"{v.message}"</div>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={v.status === "approved" ? "bg-green-100 text-green-800" : v.status === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}>{v.status}</Badge>
                      <div className="flex gap-1">
                        {v.status !== "approved" && (
                          <Button size="sm" variant="outline" onClick={() => setVolunteerStatus(v.id, "approved")} data-testid={`vol-approve-${v.id}`} className="text-green-700 border-green-300 hover:bg-green-50">Approve</Button>
                        )}
                        {v.status !== "rejected" && (
                          <Button size="sm" variant="outline" onClick={() => setVolunteerStatus(v.id, "rejected")} data-testid={`vol-reject-${v.id}`} className="text-red-700 border-red-300 hover:bg-red-50">Reject</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => delVolunteer(v.id)} data-testid={`vol-del-${v.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {volunteers.length === 0 && <div className="text-[#4A4A4A]">No volunteers yet.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
